import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Lf23Loader from "../../components/Lf23Loader";
import DatePickerModal from "../../components/DatePickerModal/DatePickerModal";
import EditorReportQuartiere from "./EditorReportQuartiere";
import { API_URL, authHeaders, nf, quartiere as etQuartiere, formatData, BadgeStatoReport, useCopia, clicFuori } from "./comuni";

/**
 * Tab "Report di quartiere": i report condivisibili verso clienti ed enti esterni.
 *
 * Elenco dei report (bozze, pubblicati, disattivati) con le azioni sul link, e l'editor
 * (EditorReportQuartiere) per comporre una bozza: quartiere + periodo -> tratte del
 * periodo con stato -> scelta di tratte, foto e KPI -> anteprima -> pubblicazione.
 *
 * Un report pubblicato e' uno SNAPSHOT: non si modifica, si duplica in una nuova bozza.
 * Il link si puo' disattivare, riattivare o rigenerare (il vecchio smette di funzionare).
 *
 * API: /api/gestione-percorsi/report-quartiere (stesso permesso della pagina).
 */

const BASE = `${API_URL}/gestione-percorsi/report-quartiere`;

// Periodo proposto per un nuovo report: gli ultimi 7 giorni conclusi (ieri e i 6 prima).
const periodoDefault = () => {
    const iso = (d) => d.toISOString().slice(0, 10);
    const al = new Date(); al.setDate(al.getDate() - 1);
    const dal = new Date(al); dal.setDate(dal.getDate() - 6);
    return { dal: iso(dal), al: iso(al) };
};

const TabReportQuartiere = ({ zona }) => {
    const [report, setReport] = useState([]);
    const [quartieri, setQuartieri] = useState([]);
    const [catalogo, setCatalogo] = useState({ kpiDisponibili: [], kpiDefault: [] });
    const [loading, setLoading] = useState(true);
    const [errore, setErrore] = useState(null);
    const [aperto, setAperto] = useState(null);       // id del report nell'editor
    const [nuovo, setNuovo] = useState(null);         // { quartiereId, dal, al } del modale "Nuovo"
    const [creando, setCreando] = useState(false);
    const [azione, setAzione] = useState(null);       // id con un'azione in corso
    const [daEliminare, setDaEliminare] = useState(null);
    const { copia, copiato } = useCopia();

    const carica = useCallback(async () => {
        setErrore(null);
        try {
            const [r, q] = await Promise.all([
                axios.get(BASE, { headers: authHeaders(), params: { zona } }),
                axios.get(`${BASE}/quartieri`, { headers: authHeaders(), params: { zona } }),
            ]);
            setReport(r.data.data || []);
            setQuartieri(q.data.quartieri || []);
            setCatalogo({ kpiDisponibili: q.data.kpiDisponibili || [], kpiDefault: q.data.kpiDefault || [] });
        } catch (e) {
            setErrore(e?.response?.data?.error || "Errore nel caricamento dei report");
        } finally {
            setLoading(false);
        }
    }, [zona]);

    useEffect(() => { carica(); }, [carica]);

    const apriNuovo = () => setNuovo({ quartiereId: quartieri[0]?.id ?? "", ...periodoDefault() });

    const creaBozza = async () => {
        if (!nuovo?.quartiereId) return;
        setCreando(true);
        try {
            const r = await axios.post(BASE, { quartiereId: Number(nuovo.quartiereId), dal: nuovo.dal, al: nuovo.al, zona }, { headers: authHeaders() });
            setNuovo(null);
            await carica();
            setAperto(r.data.id);
        } catch (e) {
            alert(e?.response?.data?.error || "Errore nella creazione del report");
        } finally {
            setCreando(false);
        }
    };

    /** Un'azione sul link (disattiva / riattiva / rigenera / duplica) dalla lista. */
    const esegui = async (id, verbo) => {
        setAzione(id);
        try {
            const r = await axios.post(`${BASE}/${id}/${verbo}`, {}, { headers: authHeaders() });
            await carica();
            if (verbo === "duplica") setAperto(r.data.id);
        } catch (e) {
            alert(e?.response?.data?.error || "Operazione non riuscita");
        } finally {
            setAzione(null);
        }
    };

    const elimina = async () => {
        if (!daEliminare) return;
        setAzione(daEliminare.id);
        try {
            await axios.delete(`${BASE}/${daEliminare.id}`, { headers: authHeaders() });
            setDaEliminare(null);
            await carica();
        } catch (e) {
            alert(e?.response?.data?.error || "Eliminazione non riuscita");
        } finally {
            setAzione(null);
        }
    };

    if (aperto != null) {
        return (
            <EditorReportQuartiere
                id={aperto}
                catalogo={catalogo}
                onChiudi={() => { setAperto(null); carica(); }}
                onDuplica={(nuovoId) => { setAperto(nuovoId); carica(); }}
            />
        );
    }

    if (loading) return <Lf23Loader testo="Caricamento report…" />;

    return (
        <div className="gp-rq">
            {errore && <div className="gp-rep-avviso"><span>{errore}</span></div>}

            <div className="toolbar">
                <button className="btn btn-primary gp-new-btn" onClick={apriNuovo} disabled={!quartieri.length} title={quartieri.length ? "" : "Nessun quartiere nell'anagrafica tratti"}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                    Nuovo report
                </button>
                <span className="results-badge">{nf(report.length)} report</span>
                <span className="spacer" />
                <button className="btn btn-ghost gp-new-btn" onClick={() => { setLoading(true); carica(); }}>Aggiorna</button>
            </div>

            {report.length === 0 ? (
                <div className="gp-rq-vuoto">
                    <strong>Nessun report ancora.</strong>
                    <span>Crea un report scegliendo quartiere e periodo: potrai selezionare tratte, foto e indicatori, vedere l’anteprima e generare un link da condividere.</span>
                </div>
            ) : (
                <div className="table-container">
                    <div className="table-scroll">
                        <table className="gp-rq-table">
                            <thead>
                                <tr>
                                    <th>Report</th>
                                    <th>Quartiere</th>
                                    <th>Periodo</th>
                                    <th>Stato</th>
                                    <th>Contenuto</th>
                                    <th>Link</th>
                                    <th className="col-actions">Azioni</th>
                                </tr>
                            </thead>
                            <tbody>
                                {report.map((r) => (
                                    <tr key={r.id}>
                                        <td>
                                            <div className="strong">{r.titolo}</div>
                                            <div className="sub">creato {formatData(String(r.creatoIl).slice(0, 10))}{r.pubblicatoIl ? ` · pubblicato ${formatData(String(r.pubblicatoIl).slice(0, 10))}` : ""}</div>
                                        </td>
                                        <td>{etQuartiere(r.quartiere)}</td>
                                        <td className="gp-rep-nowrap">{formatData(r.dal)} – {formatData(r.al)}</td>
                                        <td><BadgeStatoReport stato={r.stato} /></td>
                                        <td className="sub gp-rep-nowrap">
                                            {nf(r.riepilogo?.tratte)} tratte · {nf(r.riepilogo?.foto)} foto · {nf(r.riepilogo?.kpi)} KPI
                                            {r.riepilogo?.percentuale != null && <> · {nf(r.riepilogo.percentuale, 1)}%</>}
                                        </td>
                                        <td>
                                            {r.url ? (
                                                <div className="gp-rq-link">
                                                    <a href={r.url} target="_blank" rel="noreferrer" className={r.attivo ? "" : "spento"} title={r.attivo ? "Apri il report pubblico" : "Link disattivato"}>
                                                        /r/{r.token.slice(0, 8)}…
                                                    </a>
                                                    <button className="gp-rq-copia" onClick={() => copia(r.url, r.id)} title="Copia link">
                                                        {copiato === r.id ? "Copiato" : "Copia"}
                                                    </button>
                                                </div>
                                            ) : <span className="muted">—</span>}
                                        </td>
                                        <td className="col-actions">
                                            <div className="gp-rq-azioni">
                                                <button className="btn btn-ghost gp-new-btn" onClick={() => setAperto(r.id)}>
                                                    {r.stato === "bozza" ? "Modifica" : "Apri"}
                                                </button>
                                                {r.stato === "pubblicato" && (
                                                    <button className="btn btn-ghost gp-new-btn" disabled={azione === r.id} onClick={() => esegui(r.id, "disattiva")}>Disattiva</button>
                                                )}
                                                {r.stato === "disattivato" && (
                                                    <button className="btn btn-ghost gp-new-btn" disabled={azione === r.id} onClick={() => esegui(r.id, "riattiva")}>Riattiva</button>
                                                )}
                                                {r.stato !== "bozza" && (
                                                    <button className="btn btn-ghost gp-new-btn" disabled={azione === r.id} onClick={() => esegui(r.id, "duplica")} title="Nuova bozza con le stesse scelte">Duplica</button>
                                                )}
                                                {r.stato !== "pubblicato" && (
                                                    <button className="btn btn-ghost gp-new-btn gp-rq-elimina" disabled={azione === r.id} onClick={() => setDaEliminare(r)}>Elimina</button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Modale "Nuovo report": quartiere + periodo, il resto nell'editor */}
            {nuovo && (
                <div className="modal-overlay" {...clicFuori(() => setNuovo(null))}>
                    <div className="modal">
                        <div className="modal-header">
                            <div className="m-icon">
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
                            </div>
                            <div style={{ flex: 1 }}><h3>Nuovo report di quartiere</h3></div>
                            <button className="modal-close" onClick={() => setNuovo(null)}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="filters-grid">
                                <div className="filter-group">
                                    <label>Quartiere</label>
                                    <select value={nuovo.quartiereId} onChange={(e) => setNuovo({ ...nuovo, quartiereId: e.target.value })}>
                                        {quartieri.map((q) => <option key={q.id} value={q.id}>{q.nome}</option>)}
                                    </select>
                                </div>
                                <div className="filter-group">
                                    <label>Dal</label>
                                    <DatePickerModal value={nuovo.dal} onChange={(e) => setNuovo({ ...nuovo, dal: e.target.value })} max={nuovo.al || undefined} label="Dal" />
                                </div>
                                <div className="filter-group">
                                    <label>Al</label>
                                    <DatePickerModal value={nuovo.al} onChange={(e) => setNuovo({ ...nuovo, al: e.target.value })} min={nuovo.dal || undefined} label="Al" />
                                </div>
                            </div>
                            <p className="sub">Il periodo proposto è l’ultima settimana conclusa. Nel passo successivo scegli tratte, foto e indicatori da mostrare.</p>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-ghost" onClick={() => setNuovo(null)}>Annulla</button>
                            <button className="btn btn-primary" onClick={creaBozza} disabled={creando || !nuovo.quartiereId || !nuovo.dal || !nuovo.al}>
                                {creando ? "Creazione…" : "Continua"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {daEliminare && (
                <div className="modal-overlay" {...clicFuori(() => setDaEliminare(null))}>
                    <div className="modal">
                        <div className="modal-header">
                            <div className="m-icon" style={{ background: "linear-gradient(135deg, #EF4444, #DC2626)" }}>
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                            </div>
                            <div style={{ flex: 1 }}><h3>Eliminare il report?</h3></div>
                        </div>
                        <div className="modal-body">
                            <p className="modal-text"><strong>{daEliminare.titolo}</strong> ({formatData(daEliminare.dal)} – {formatData(daEliminare.al)}) verrà eliminato definitivamente{daEliminare.stato === "disattivato" ? ", insieme al suo link" : ""}.</p>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-ghost" onClick={() => setDaEliminare(null)}>Annulla</button>
                            <button className="btn btn-danger" onClick={elimina} disabled={azione === daEliminare.id}>Elimina</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TabReportQuartiere;
